import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLaptopCode, faGraduationCap, faHeadphonesSimple, faBookOpen } from '@fortawesome/free-solid-svg-icons';
import {
  faWindows as fabFaWindows,
  faApple as fabFaApple,
  faLinux as fabFaLinux,
  faAndroid as fabFaAndroid,
} from '@fortawesome/free-brands-svg-icons';
import includes from 'lodash/includes';
import get from 'lodash/get';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { connect } from 'react-redux';
import differenceInDays from 'date-fns/difference_in_days';
import { Col } from 'reactstrap';

import { countryCode } from 'Helpers/Geo/Country';
import { toggleCheckoutModal } from 'Redux/ducks/modal';
import AddToCartBtn from 'Components/Button/Cart/AddToCartCardBtn';
import store from 'Redux/store';
import DlcBadge from 'Components/Label/DlcBadge';
import ProductCover from 'Components/Media/Image/ProductCover';
import HitCardButtons from 'Components/Card/HitCard/HitCardButtons';
import HitCardInCart from 'Components/Card/HitCard/HitCardInCart';
import CardLabel from 'Components/Label/CardLabel';
import Drm from 'Components/ProductInfo/Drm';
import PriceContainer from 'Components/Price/PriceContainer';
import FlashCardProgress from 'Components/Progress/FlashCardProgress';
import CardWishlistBtn from 'Components/Button/Card/CardWishlistBtn';
import QuickLook from 'Components/Card/HitCard/HitCardQuickLook';
import FormatProductName from 'Helpers/Format/FormatProductName';
import HitLinkTo from 'Helpers/Product/HitLinkTo';
import useViewport from 'Hooks/useViewport';
import imageSizes from 'Helpers/Image/image-sizes';
import NewCountdownTimer from 'Components/Timer/CountdownTimer/CountdownTimer';

const messages = defineMessages({
  addToCart: {
    id: 'hitcardf.button.addtocart',
    defaultMessage: 'Add to cart',
  },
});

function HitCard({
  hit,
  siteTheme,
  lgSize,
  mdSize,
  smSize,
  xsSize,
  bigCard,
  flashDeal,
  showFlashDealBar, // Allows regular hits to show bar as the flashDeal prop changes data
  hitCardLightTheme,
  hitCardDarkTheme,
  lazy,
  hideDesc,
  classes,
  sizesOverride,
  place,
  displayName,
  wishlistId,
  wishlistSlugs,
  inCart,
  labelOverride,
  hitCardContainerTag,
  hitCardNameTag,
  clickFunction,
  inLibrary,
  inSteamLibrary,
  authenticated,
  coverTypeOverride, // Overrides the cover type (square, portrait, etc)
  adaptiveSize, // Used when portrait and square images need to be shown at their natural size, otherwise the image will be shown as 16x9 with a blurred background
  hideHitCardButtons, // Hides the hover buttons on desktop and adds a button below the card
  tierIndex,
  intl,
  addButtonOverride,
}) {
  const [isHovering, setIsHovering] = useState(false);
  const { isAboveSm, isBelowLg } = useViewport();
  const { locale } = window;

  const { formatMessage } = intl;

  let startingDate = hit.available_valid_from;
  let endingDate = hit.available_valid_until;
  let OS = hit.operating_systems || [];
  const discountPercentOverride = hit.discount_percent_override;

  const isSrpBundle = get(hit, 'is_srp_bundle', false);
  const sdescs = get(hit, 'sdescs', {});
  const features = get(hit, 'features', []);
  const lowerCaseFeatures = features.map(feature => feature.toLowerCase());
  const platforms = hit.platforms;

  let freeToPlayTag = false;
  if (includes(lowerCaseFeatures, 'free to play')) {
    freeToPlayTag = true;
  }

  let noGermanRelease = false;
  if (countryCode === 'DE' && hit.no_release_date) {
    noGermanRelease = true;
  }

  const isPromotion = (hit.type === 'pick-and-mix');

  const onLinkClick = () => {
    if (['Mega Menu', 'Deep Discount Exit Intent'].includes(place)) {
      clickFunction();
    }

    if (place === 'Checkout Modal') {
      store.dispatch(toggleCheckoutModal());
    }
  };

  let validDrm = '';
  let filteredDrm = [];
  if (hit && hit.drm && !flashDeal) filteredDrm = hit.drm.filter(d => d !== 'voucher' && d !== 'drm free');
  if (filteredDrm.length === 1) validDrm = filteredDrm[0];
  if (flashDeal) {
    let validPlatforms = [];
    validPlatforms = hit.drm && Object.keys(hit.drm).filter(d => hit.drm[d]);
    filteredDrm = !isEmpty(validPlatforms) && validPlatforms.filter(platform => platform !== 'voucher');

    validDrm = filteredDrm[0];

    startingDate = hit.available_valid_from / 1000;
    endingDate = hit.available_valid_until / 1000;

    OS = [];
    if (platforms && platforms.windows) OS.push('windows');
    if (platforms && platforms.mac) OS.push('mac');
    if (platforms && platforms.linux) OS.push('linux');
  }

  let discountPercent = hit.discount_percent;

  if (hit.type === 'pick-and-mix') {
    discountPercent = hit.pnm_saving;
  }

  OS.sort().reverse();

  const name = FormatProductName(hit.name, hit.type);

  const applyHideOverlay = !wishlistSlugs.includes(hit.slug)
    || inCart;

  let cardTheme = 'dark';
  if (hitCardLightTheme || siteTheme === 'light') cardTheme = 'light';
  if (hitCardDarkTheme) cardTheme = 'dark';

  const TagName = hitCardNameTag;

  let productCoverSizes = imageSizes(lgSize, mdSize, smSize, xsSize);

  if (sizesOverride) productCoverSizes = sizesOverride;

  let cardType = 'standard';
  if (hit.display_type === 'comic-bundle' || hit.display_type === 'comic') cardType = 'comic';
  if (hit.display_type === 'book-bundle' || hit.display_type === 'book') cardType = 'book';
  if (hit.display_type === 'elearning-bundle' || hit.display_type === 'elearning') cardType = 'elearning';
  if (hit.display_type === 'audio-bundle' || hit.display_type === 'audio') cardType = 'audio';
  if (hit.display_type === 'software-bundle' || hit.display_type === 'software') cardType = 'software';
  if (hit.display_type === 'graphic-novel') cardType = 'graphic-novel';

  let showAddButton = false;
  if (!hit.tiered && !hit.presale && !hit.free_to_play && !hit.pay_what_you_want && hit.type !== 'pick-and-mix') {
    showAddButton = true;
  }

  if (tierIndex === 0) {
    showAddButton = true;
  }

  showAddButton = addButtonOverride ? true : showAddButton && isBelowLg;

  let coverType = '';
  if (hit.cover_type) coverType = hit.cover_type;
  if (coverTypeOverride) coverType = coverTypeOverride;

  // <Link to={HitLinkTo(hit)} className="hitCardStripe__content__purchase__price faux-block-link__overlay-link" onClick={onLinkClick}></Link>

  let _labelOverride = labelOverride;


  if (!_labelOverride) {
    if (inLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inLibrary';
    } else if (inSteamLibrary && authenticated && !hit.mystery) {
      _labelOverride = 'inSteamLibrary';
    }
  }

  const daysLeft = differenceInDays(new Date(endingDate * 1000), new Date());
  let showTimer = !isSrpBundle && (hit.type === 'bundle' || hit.type === 'pick-and-mix') && daysLeft < 90;
  if (tierIndex === 0) showTimer = false;
  const canShowByobQuickLook = (isPromotion || hit.type === 'bundle') && !isBelowLg && !isEmpty(hit.bundle_covers) && !hit.mystery;

  return (
    <Col
      tag={hitCardContainerTag}
      lg={lgSize}
      md={mdSize}
      sm={smSize}
      xs={xsSize}
      className={classNames(
        'HitCardContainer',
        { classes: classes !== '' },
      )}
    >
      <div
        className={classNames(
          'HitCard',
          { 'HitCard--dark': cardTheme === 'dark' },
          { 'HitCard--light': cardTheme === 'light' },
          { 'HitCard--square': coverType === 'square' },
          { 'big-hit-card': bigCard },
          { 'flash-hit-card': flashDeal || showFlashDealBar },
          'faux-block-link',
        )}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <CardLabel
          slug={hit.slug}
          bestDiscount={hit.best_ever}
          preorderDate={hit.release_date}
          startingDate={startingDate}
          endingDate={endingDate}
          type={hit.type}
          displayType={hit.display_type}
          presale={hit.presale}
          discountPercent={hit.discount_percent}
          flashDealCard={flashDeal || showFlashDealBar}
          noReleaseDate={hit.no_release_date}
          collections={hit.collections || []}
          systemDiscount={hit.system_discount}
          labelOverride={_labelOverride}
          earlyAccessDate={hit.preorder_playable_from}
          isStarDeal={hit.star_deal}
          sponsoredDiscount={hit.sponsored_discount}
        />

        {isHovering &&
          <div className="hit-card-overlay faux-block-link">
            <div className={`overlayContent ${applyHideOverlay ? 'hide-overlayContent' : ''}`}>
              {hit.presale && (!freeToPlayTag || hit.free_to_play) && !isPromotion &&
                <CardWishlistBtn
                  productId={hit.product_id}
                  slug={hit.slug} hit={hit}
                  place={place}
                />
              }
              {!hit.presale && !isPromotion &&
                <CardWishlistBtn
                  productId={hit.product_id}
                  slug={hit.slug} hit={hit}
                  place={place}
                />
              }

              <div className="overlay-content-container">
                <TagName className="hit-card-game-name">
                  <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link" onClick={onLinkClick}>
                    {name}
                  </Link>
                </TagName>

                <div>
                  {!hideHitCardButtons &&
                    <HitCardButtons
                      hit={hit}
                      noGermanRelease={noGermanRelease}
                      place={place}
                      payWhatYouWant={hit.pay_what_you_want}
                      displayName={displayName}
                      wishlistId={wishlistId}
                      clickFunction={clickFunction}
                    />
                  }
                  {canShowByobQuickLook &&
                    <div className="quicklook-container">
                      <QuickLook hit={hit} clickFunction={clickFunction} place={place} />
                    </div>
                  }
                </div>

                {flashDeal && !!hit.addVoucher &&
                  <div className="price-asterix">
                    <FormattedMessage id="hitcardf.priceasterix" defaultMessage="*Price includes coupon" />
                  </div>
                }
              </div>
            </div>
          </div>
        }

        <div key={hit.product_id} className="HitCard__main">
          <HitCardInCart slug={hit.slug} />
          <Link to={HitLinkTo(hit)} className="HitCard__main__cover" onClick={onLinkClick}>
            <ProductCover
              cover={hit.cover}
              portraitCover={hit.portrait_cover}
              alt={hit.cover_alt_text || `Product cover for ${name}`}
              sizesOverride={productCoverSizes}
              displayType={hit.display_type}
              coverType={coverType}
              lazy={lazy || place === 'Mega Menu'}
              adaptiveSize={adaptiveSize}
            />
          </Link>


          <div className="hitCardStripe">
            <TagName className="hitCardStripe__seoName">
              {name}
            </TagName>

            {bigCard && !hideDesc &&
              <div className="big-card-sdesc d-flex align-items-center">
                {sdescs[locale] || hit.sdesc}
              </div>
            }

            <div className="hitCardStripe__content">
              <div className="hitCardStripe__content__purchase">
                {!showTimer &&
                  <Link to={HitLinkTo(hit)} className="hitCardStripe__content__purchase__price faux-block-link__overlay-link" onClick={onLinkClick}>
                    {hit.presale && (!freeToPlayTag || hit.free_to_play) &&
                      <div className="hitCardStripe__content__purchase__price__text">
                        <FormattedMessage id="hitcardf.availablesoon" defaultMessage="Available Soon" />
                      </div>
                    }

                    {(hit.free_to_play || (hit.presale && freeToPlayTag)) &&
                      <div className="hitCardStripe__content__purchase__price__text">
                        <FormattedMessage id="hitcardf.freetoplay" defaultMessage="Free to Play" />
                      </div>
                    }

                    {!hit.presale && !hit.free_to_play && !hit.hide_price &&
                      <PriceContainer
                        discountPercent={discountPercent}
                        discountPercentOverride={discountPercentOverride}
                        drm={validDrm}
                        fullPrice={hit.fullPrice}
                        hideDiscount={hit.hide_discount}
                        lightTheme={hitCardLightTheme}
                        mystery={hit.mystery}
                        payWhatYouWant={hit.pay_what_you_want}
                        price={hit.price}
                        showAsterisk={!!hit.addVoucher}
                        slug={hit.slug}
                        tiered={hit.tiered}
                        type={hit.type}
                      />
                    }
                  </Link>
                }

                {showTimer &&
                  <Link to={HitLinkTo(hit)} className="faux-block-link__overlay-link" onClick={onLinkClick}>
                    <NewCountdownTimer
                      cardTimer
                      value={endingDate * 1000}
                    />
                  </Link>
                }

                {showAddButton &&
                  <AddToCartBtn
                    newIcon
                    showInCartLink
                    hideButtonText
                    id={hit.product_id}
                    slug={hit.slug}
                    voucherToAdd={hit.addVoucher}
                    tierIndex={tierIndex}
                  />
                }
              </div>

              {isAboveSm &&
                <div className="hitCardStripe__content__icons">
                  {(validDrm || cardType === 'standard') &&
                    <React.Fragment>
                      {(!isEmpty(OS) || validDrm === 'utalk') &&
                        <div className="hitCardStripe__content__icons__os">
                          {includes(OS, 'windows') &&
                            <FontAwesomeIcon icon={fabFaWindows} />
                          }
                          {includes(OS, 'mac') &&
                            <FontAwesomeIcon icon={fabFaApple} />
                          }
                          {includes(OS, 'linux') &&
                            <FontAwesomeIcon icon={fabFaLinux} />
                          }
                          {validDrm === 'utalk' &&
                            <FontAwesomeIcon icon={fabFaAndroid} />
                          }
                        </div>
                      }
                      {validDrm &&
                        <Drm drmType={validDrm} colorOverride={cardTheme === 'dark' ? 'light' : 'dark'} />
                      }
                      {hit.display_type === 'dlc' &&
                        <DlcBadge />
                      }
                    </React.Fragment>
                  }
                  {!validDrm && (cardType === 'comic' || cardType === 'book' || cardType === 'elearning' || cardType === 'audio' || cardType === 'software' || cardType === 'graphic-novel') &&
                    <div className="hitCardStripe__content__icons__nonGame">
                      {cardType === 'comic' &&
                        <>
                          <FontAwesomeIcon icon={faBookOpen} />
                          <FormattedMessage id="hitcardf.comic-bundle" defaultMessage="Comic" />
                        </>
                      }
                      {cardType === 'book' &&
                        <>
                          <FontAwesomeIcon icon={faBookOpen} />
                          <FormattedMessage id="hitcardf.book-bundle" defaultMessage="eBook" />
                        </>
                      }
                      {cardType === 'elearning' &&
                        <>
                          <FontAwesomeIcon icon={faGraduationCap} />
                          <FormattedMessage id="hitcardf.elearning-bundle" defaultMessage="eLearning" />
                        </>
                      }
                      {cardType === 'audio' &&
                        <>
                          <FontAwesomeIcon icon={faHeadphonesSimple} />
                          <FormattedMessage id="hitcardf.audio" defaultMessage="Audio" />
                        </>
                      }
                      {cardType === 'software' &&
                        <>
                          <FontAwesomeIcon icon={faLaptopCode} />
                          <FormattedMessage id="hitcardf.software" defaultMessage="Software" />
                        </>
                      }
                      {cardType === 'graphic-novel' &&
                        <>
                          <FontAwesomeIcon icon={faBookOpen} />
                          <FormattedMessage id="hitcardf.graphic-novel" defaultMessage="Graphic Novel" />
                        </>
                      }
                    </div>
                  }
                </div>
              }
            </div>
          </div>
          {(flashDeal || showFlashDealBar) &&
            <FlashCardProgress startingDate={startingDate} endingDate={endingDate} />
          }
          {!isBelowLg && hideHitCardButtons &&
            <div className="HitCard__bottomButton">
              <AddToCartBtn
                showInCartLink
                id={hit.product_id}
                slug={hit.slug}
                voucherToAdd={hit.addVoucher}
                buttonText={formatMessage(messages.addToCart)}
                tierIndex={tierIndex}
              />
            </div>
          }
        </div>
      </div>
    </Col>
  );
}

HitCard.propTypes = {
  bigCard: PropTypes.bool,
  classes: PropTypes.string,
  clickFunction: PropTypes.func,
  displayName: PropTypes.string,
  flashDeal: PropTypes.bool,
  hideDesc: PropTypes.bool,
  inCart: PropTypes.bool,
  inLibrary: PropTypes.bool,
  inSteamLibrary: PropTypes.bool,
  hit: PropTypes.shape({}),
  hitCardContainerTag: PropTypes.string,
  hitCardDarkTheme: PropTypes.bool,
  hitCardLightTheme: PropTypes.bool,
  hitCardNameTag: PropTypes.string,
  labelOverride: PropTypes.string,
  lgSize: PropTypes.number,
  lazy: PropTypes.bool,
  mdSize: PropTypes.number,
  place: PropTypes.string,
  showFlashDealBar: PropTypes.bool,
  sizesOverride: PropTypes.string,
  smSize: PropTypes.number,
  wishlistId: PropTypes.string,
  wishlistSlugs: PropTypes.arrayOf(PropTypes.string),
  xsSize: PropTypes.number,
  authenticated: PropTypes.bool,
  coverTypeOverride: PropTypes.string,
  adaptiveSize: PropTypes.bool,
  hideHitCardButtons: PropTypes.bool,
  tierIndex: PropTypes.number,
};

HitCard.defaultProps = {
  bigCard: false,
  classes: '',
  clickFunction: () => { },
  displayName: '',
  flashDeal: false,
  hideDesc: false,
  inCart: false,
  inLibrary: false,
  inSteamLibrary: false,
  hit: {},
  hitCardContainerTag: 'div',
  hitCardDarkTheme: false,
  hitCardLightTheme: false,
  hitCardNameTag: 'div',
  labelOverride: '',
  lgSize: 3,
  lazy: false,
  location: {},
  mdSize: 4,
  place: '',
  showFlashDealBar: false,
  sizesOverride: null,
  smSize: 4,
  wishlistId: '',
  wishlistSlugs: [],
  xsSize: 6,
  authenticated: false,
  coverTypeOverride: null,
  adaptiveSize: false,
  hideHitCardButtons: false,
  tierIndex: null,
};

const mapStateToProps = ({
  auth: { _id: userId, authenticated },
  wishlist: { allSlugs: wishlistSlugs },
  cart: { allSlugs: cartSlugs },
  siteTheme,
  userLibrary,
  steamLibrary,
  userRecommendations: { longTailRecommendations },

}, { hit: { name, slug } }) => ({
  userId,
  authenticated,
  wishlistSlugs,
  inCart: cartSlugs.includes(slug),
  siteTheme,
  inLibrary: userLibrary.includes(name),
  inSteamLibrary: steamLibrary.includes(name),
  inRecommended: (longTailRecommendations || []).some(r => r.slug === slug),
});

export default connect(mapStateToProps)(injectIntl(HitCard));
